import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  SelectChangeEvent,
  Step,
  StepButton,
  Stepper,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Customer, ReceiptItem } from './models';
import PeopleIcon from '@mui/icons-material/People';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { flexbox } from '@mui/system';
import { useAlertSnackBar } from 'src/hooks/useAlertSnackBar';
import { AlertSnackBarSeverity } from 'src/constants';
import AlertSnackBar from 'src/components/AlertSnackBar/AlertSnackBar';
import { isString } from 'util';
import { error } from 'console';
import CollectCustomers from './components/CollectCustomers';
import CollectItems from './components/CollectItems';
import CollectReceiptDetails from './components/CollectReceiptDetails';
import { dollarFormatter } from './utils';
import SplitReceiptDisplay from './components/SplitReceiptDisplay';

//
//
//           Plan:
//             needs:
//               1) receipt info
//                   (now): input items with respective prices, total (w/ tax), tip
//                   (future): take pic of receipt with https://www.veryfi.com/demo/ to get all info
//               2) people info
//                   assign people to each item
//               3) calculate
//
//

// TODO: store states in session storage so even if browser refreshes, the data is still there

const ReceiptSplitter: React.FC = () => {
  const WorkflowStep = {
    GET_CUSTOMERS: 'Who Ate?',
    GET_RECEIPT_ITEMS: "What'd You Ate?",
    GET_TOTALS: 'Totals',
    DISPLAY_SPLITS: 'View Final Splits',
  };

  const WorkflowChangeDirection = {
    BACK: 'Back',
    NEXT: 'Next'
  }

  const {
    alertSnackBarOpen,
    alertSnackBarDuration,
    alertSnackBarSeverity,
    alertSnackBarMessage,
    openAlertSnackBar,
    setAlertSnackBarOpen,
  } = useAlertSnackBar();
  const theme = useTheme();
  const [customers, setCustomers] = useState<Array<Customer>>([]);
  const [items, setItems] = useState<Array<ReceiptItem>>([
    
  ]);

  const [total, setTotal] = useState<string>('');
  const [tip, setTip] = useState<string>('');

  const getWorkflowStepIndex = (workflowStep: string) => {
    return Object.values(WorkflowStep).indexOf(workflowStep);
  };

  const [activeWorkflowStep, setActiveWorkflowStep] = useState(
    getWorkflowStepIndex(WorkflowStep.GET_CUSTOMERS),
  );

  const isStringValidFloat = (floatString: string) => {
    return !isNaN(Number(floatString)) && !isNaN(parseFloat(floatString));
  };

  const itemsValid = (receiptItems: Array<ReceiptItem>) => {
    // at least one item must be added
    const anItemExists = receiptItems.length !== 0;
    if (!anItemExists) {
      return {
        invalidItemIndex: 0,
        errorMessage: `Must Add At Least One Item`,
      };
    }

    // i.e. that price is a valid field. that there is at least a letter in item name. that there is at least one customer assigned
    const invalidItemNameFound = receiptItems.findIndex(
      (item) => item.name.trim().length === 0,
    );
    if (invalidItemNameFound !== -1) {
      const invalidItemIndex = invalidItemNameFound;
      return {
        invalidItemIndex: invalidItemIndex,
        errorMessage: `Input An Item Name For Item ${invalidItemIndex + 1}`,
      };
    }

    const invalidItemPriceFound = receiptItems.findIndex(
      (item) => !isStringValidFloat(item.price),
    );
    if (invalidItemPriceFound !== -1) {
      const invalidItemIndex = invalidItemPriceFound;
      return {
        invalidItemIndex: invalidItemIndex,
        errorMessage: `Input Valid Number For Price Of Item ${
          invalidItemIndex + 1
        }`,
      };
    }

    const invalidItemCustomersFound = receiptItems.findIndex(
      (item) => item.customers.length === 0,
    );
    if (invalidItemCustomersFound !== -1) {
      const invalidItemIndex = invalidItemCustomersFound;
      return {
        invalidItemIndex: invalidItemIndex,
        errorMessage: `Assign At Least One Customer For Item ${
          invalidItemIndex + 1
        }`,
      };
    }

    return { invalidItemIndex: -1, errorMessage: '' };
  };

  const handleWorkflowStepChange = (
    index: number,
    changeDirection: string = WorkflowChangeDirection.BACK,
  ) => {
    const newActiveWorkflowStep = index;

    // only need to validate data if moving to next step. 
    // do not need to validate data if moving backwards
    if (changeDirection === WorkflowChangeDirection.NEXT) {
      if (
        activeWorkflowStep === getWorkflowStepIndex(WorkflowStep.GET_CUSTOMERS)
      ) {
        // at least one customer must exist
        const aCustomerExists = customers.reduce(
          (doesACustomerExist, customer) => {
            return doesACustomerExist || customer.name.length !== 0;
          },
          false,
        );
        if (!aCustomerExists) {
          openAlertSnackBar(
            6000,
            AlertSnackBarSeverity.ERROR,
            'You Must Add At Least One Customer',
          );
          return;
        }

        // check for duplicates. names must be unique.
        const duplicateExists = customers.reduce(
          (duplicateCustomerExists, currCustomer, currCustomerIndex) => {
            return (
              duplicateCustomerExists ||
              customers.findIndex(
                (customer) =>
                  customer.name.trim().toLowerCase() ===
                  currCustomer.name.trim().toLowerCase(),
              ) !== currCustomerIndex
            );
          },
          false,
        );

        if (duplicateExists) {
          openAlertSnackBar(
            6000,
            AlertSnackBarSeverity.ERROR,
            'You Cannot Have Duplicate Names',
          );
          return;
        }

      }

      // if currently on the screen where user inputs items
      if (
        activeWorkflowStep ===
        getWorkflowStepIndex(WorkflowStep.GET_RECEIPT_ITEMS)
      ) {
        const itemsAreValid = itemsValid(items);
        if (itemsAreValid.invalidItemIndex !== -1) {
          openAlertSnackBar(
            6000,
            AlertSnackBarSeverity.ERROR,
            itemsAreValid.errorMessage,
          );
          return;
        }
      }

      if (
        activeWorkflowStep === getWorkflowStepIndex(WorkflowStep.GET_TOTALS)
      ) {
        let errorMessage = '';
        if (!isStringValidFloat(total)) {
          errorMessage = 'Total Value Must Be A Number';
        } else if (!isStringValidFloat(tip)) {
          errorMessage = 'Tip Value Must Be A Number';
        }
        // console.log(isStringValidFloat(total));
        if (errorMessage !== '') {
          openAlertSnackBar(6000, AlertSnackBarSeverity.ERROR, errorMessage);
          return;
        }
      }
    }

    setActiveWorkflowStep(newActiveWorkflowStep);
  };

  const getWorkflowSteps = () => {
    return Object.values(WorkflowStep);
  };

  const notAtLastStep = () => {
    return activeWorkflowStep < Object.keys(WorkflowStep).length - 1;
  };

  const notAtFirstStep = () => {
    return activeWorkflowStep > 0;
  };

  const getCustomerItemMapping = () => {
    let customerItemMapping: { [key: string]: Array<ReceiptItem> } = {};
    items.forEach((item) => {
      item.customers.forEach((customer) => {
        if (customer.name in customerItemMapping) {
          customerItemMapping[customer.name] = customerItemMapping[
            customer.name
          ].concat([item]);
        } else {
          customerItemMapping[customer.name] = [item];
        }
      });
    });
    return customerItemMapping;
  };

  const getGrandTotal = () => {
    return (
      parseFloat(total.trim().length === 0 ? '0' : total) +
      parseFloat(tip.trim().length === 0 ? '0' : tip)
    );
  };

  const getReceiptSubtotal: () => number = () => {
    return items
      .filter((item) => item.price.trim().length !== 0)
      .reduce((subtotalAccumulator: number, item: ReceiptItem) => {
        return subtotalAccumulator + parseFloat(item.price);
      }, 0);
  };

  const getCustomerTotal = (customerItems: Array<ReceiptItem>) => {
    const customerSubtotal = customerItems.reduce((itemsTotal, item) => {
      // the price that is a portion of the item price depending on how many people shared it
      const customerItemPrice = parseFloat(item.price) / item.customers.length;
      return itemsTotal + customerItemPrice;
    }, 0);
    const customerPercentageOfBill = customerSubtotal / getReceiptSubtotal();
    // console.log(customerPercentageOfBill)
    const totalPaid = getGrandTotal();
    const customerTotal = totalPaid * customerPercentageOfBill;
    return customerTotal;
  };


  return (
    <Box
      sx={{
        margin: '32px 0',
        padding: '16px',
        minHeight: '67vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'start',
          gap: '16px',
          padding: '32px 16px',
          maxWidth: '750px',
        }}
      >
        <Typography
          variant='h6'
          sx={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            margin: '0 0 16px 0',
          }}
        >
          <strong>Receipt Splitter</strong>
        </Typography>
        <Stepper
          // orientation='vertical'
          // nonLinear
          activeStep={activeWorkflowStep}
        >
          {getWorkflowSteps().map((label, index) => (
            <Step
              key={label}
              // completed={completed[index]}
            >
              <StepButton
                onClick={() => {
                  handleWorkflowStepChange(index);
                }}
              >
                {label}
              </StepButton>
            </Step>
          ))}
        </Stepper>
        {/* display the content related to current step selected */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            // maxHeight: '70vh',
            overflowY: 'auto',
            width: '100%',
          }}
        >
          {activeWorkflowStep ===
            getWorkflowStepIndex(WorkflowStep.GET_CUSTOMERS) && (
            <CollectCustomers
              customers={customers}
              setCustomers={setCustomers}
            />
          )}
          {activeWorkflowStep ===
            getWorkflowStepIndex(WorkflowStep.GET_RECEIPT_ITEMS) && (
            <CollectItems
              items={items}
              setItems={setItems}
              customers={customers}
            />
          )}
          {activeWorkflowStep ===
            getWorkflowStepIndex(WorkflowStep.GET_TOTALS) && (
            <CollectReceiptDetails
              tip={tip}
              setTip={setTip}
              total={total}
              setTotal={setTotal}
              getReceiptSubtotal={getReceiptSubtotal}
              getGrandTotal={getGrandTotal}
            />
          )}
          {activeWorkflowStep ===
            getWorkflowStepIndex(WorkflowStep.DISPLAY_SPLITS) && (
            <SplitReceiptDisplay
              getCustomerItemMapping={getCustomerItemMapping}
              getCustomerTotal={getCustomerTotal}
            />
          )}
        </Box>
        {/* display navigation buttons for stepper */}
        <Grid
          container
          spacing={1}
        >
          <Grid
            item
            xs={6}
          >
            {notAtFirstStep() && (
              <Button
                fullWidth
                variant='outlined'
                // sx={{ textDecoration: 'underline'}}
                onClick={() => {
                  const newActiveWorkflowStep = activeWorkflowStep - 1;
                  handleWorkflowStepChange(newActiveWorkflowStep);
                }}
              >
                Back
              </Button>
            )}
          </Grid>
          <Grid
            item
            xs={6}
          >
            {notAtLastStep() && (
              <Button
                fullWidth
                variant='contained'
                onClick={() => {
                  const newActiveWorkflowStep = activeWorkflowStep + 1;
                  handleWorkflowStepChange(
                    newActiveWorkflowStep,
                    WorkflowChangeDirection.NEXT,
                  );
                }}
              >
                Next
              </Button>
            )}
          </Grid>
          <AlertSnackBar
            openState={alertSnackBarOpen}
            toggleOpenState={() => {
              setAlertSnackBarOpen(!alertSnackBarOpen);
            }}
            duration={alertSnackBarDuration}
            severity={alertSnackBarSeverity}
            message={alertSnackBarMessage}
          />
        </Grid>
      </Paper>
    </Box>
  );
};

export default ReceiptSplitter;
